// The HUD over every room (spec 3.6), staged as things in the building (M10 track D):
// - the map button is the building directory, a framed floor plan on the wall with a red "you are here" dot;
// - the room's name is a brass sign hung over the doorway on two rods, and it swings a little when you arrive;
// - the player chip is your bar card on a lanyard, the thing you carry through the building.
// The card opens the one menu that holds every destination the old ledger nav did (a paper directory leaflet), each
// a GuardedLink so a live argument can intercept it, plus Night and Sound. Accessible names and test ids are unchanged.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { GuardedLink } from '../../lib/leaveGuard';
import { useBackToClose } from '../../lib/useBackToClose';
import { useTheme, toggleTheme } from '../../lib/theme';
import { useAuth } from '../../hooks/useAuth';
import { Icon } from '../../ui/Icon';
import { useStage } from '../../world/WorldStage';
import { ROOM_LABELS, roomForPath } from '../../world/rooms';
import { setSfxEnabled, usePrefs } from '../../world/prefs';
import { SFX } from '../../world/sfx';
import './hud.css';

export const NAV_LINKS: Array<{ to: string; label: string }> = [
  { to: '/home',         label: 'The Docket' },
  { to: '/record',       label: 'Your Record' },
  { to: '/achievements', label: 'The Register' },
  { to: '/leaderboard',  label: 'Leaderboard' },
  { to: '/court',        label: 'The Court' },
  { to: '/guide',        label: 'Guide' },
];

export const MORE_LINKS: Array<{ to: string; label: string }> = [
  { to: '/about',    label: 'About' },
  { to: '/settings', label: 'Settings' },
  { to: '/terms',    label: 'Terms & Conditions' },
];

const MENU_ID = 'hud-menu';

export function formatCredit(xp: number | null | undefined): string {
  return (xp ?? 0).toLocaleString('en-US');
}

/** Where each room sits on the directory's plan (17 x 11 cells, east at the top, as on the visitor map). Rooms on
 *  other floors are marked where they are in the footprint: the floors stack. */
const HERE: Record<string, [number, number]> = {
  courtroom: [8, 4], greathall: [8, 9], robing: [6, 1], conference: [10, 1], chambers: [2, 1], chief: [14, 1],
  marshal: [1, 4], lounge: [15, 4], clerk: [14, 1], pio: [2, 1], police: [15, 9], giftshop: [1, 4], cafe: [1, 8],
  lowerhall: [8, 9], library: [8, 8], hoops: [8, 4], front: [8, 10], plaza: [8, 10],
};

/** The building in seventeen by eleven cells: wings, the four courtyards, the courtroom, the Great Hall and its runner. */
export function FloorPlan({ here }: { here?: string | null }) {
  const dot = here ? HERE[here] : undefined;
  const R = (x: number, y: number, w: number, h: number, c: string) => <rect x={x} y={y} width={w} height={h} fill={c} />;
  return (
    <svg className="plan" viewBox="0 0 17 11" shapeRendering="crispEdges" aria-hidden="true">
      {R(0, 0, 17, 11, 'var(--ink-px)')}
      {R(1, 1, 15, 9, 'var(--pap1)')}
      {R(3, 3, 2, 3, 'var(--green3)')}{R(12, 3, 2, 3, 'var(--green3)')}
      {R(3, 7, 2, 3, 'var(--green3)')}{R(12, 7, 2, 3, 'var(--green3)')}
      {R(5, 3, 7, 3, 'var(--vel2)')}
      {R(6, 7, 5, 3, 'var(--mar4)')}{R(8, 7, 1, 4, 'var(--vel3)')}
      {R(1, 2, 15, 1, 'var(--mar1)')}{R(5, 6, 7, 1, 'var(--mar1)')}
      {R(6, 10, 5, 1, 'var(--mar2)')}
      {dot && <rect className="plan-here" x={dot[0]} y={dot[1]} width={1} height={1} fill="var(--lamp-red)" />}
    </svg>
  );
}

/** The map button: the building directory, a framed floor plan on the wall. Also used by the argument's own HUD,
 *  which can pass the room it is in for the red dot (it has no router location of its own to read). */
export function MapButton({ room }: { room?: string | null } = {}) {
  const stage = useStage();
  return (
    <button
      type="button"
      className="hud-directory"
      aria-label="Building map"
      aria-haspopup="dialog"
      data-testid="map-button"
      onClick={() => { SFX.move(); stage?.setMapOpen(true); }}
    >
      <span className="hud-directory-board" aria-hidden="true">
        <FloorPlan here={room} />
        <span className="hud-directory-label">Directory</span>
      </span>
    </button>
  );
}

export default function Header({ plaque, hidden = false }: { plaque?: ReactNode; hidden?: boolean } = {}) {
  const location = useLocation();
  const theme = useTheme();
  const { account, status } = useAuth();
  const prefs = usePrefs();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const items = useRef<Array<HTMLElement | null>>([]);
  const close = useCallback(() => setOpen(false), []);

  useBackToClose(open, close);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => { if (open) items.current[0]?.focus(); }, [open]);
  // With the leaflet in hand the room's own papers step back (hud.css), so the leaflet never lies over their text.
  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    root.classList.add('hud-menu-open');
    return () => root.classList.remove('hud-menu-open');
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: Event) => { if (!wrapRef.current?.contains(e.target as Node)) close(); };
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', away);
    window.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', away); window.removeEventListener('keydown', key); };
  }, [open, close]);

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
  };
  const onMenuKey = (e: React.KeyboardEvent) => {
    const list = items.current.filter((x): x is HTMLElement => !!x);
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
    if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
    if (e.key === 'End') { e.preventDefault(); list[list.length - 1]?.focus(); }
    if (e.key === 'Tab') close();
  };

  const name = account?.display_name?.trim() || 'Counsel';
  const roomKey = roomForPath(location.pathname);
  const room = ROOM_LABELS[roomKey] ?? '';
  let n = 0;
  const reg = (el: HTMLElement | null, k: number) => { items.current[k] = el; };

  return (
    // Hidden, not unmounted, while the map is open: the map button must still exist to take focus back.
    <header className="hud" data-testid="hud" style={hidden ? { visibility: 'hidden' } : undefined}>
      <div className="hud-left"><MapButton room={roomKey} /></div>
      <div className="hud-centre">
        {/* keyed by the room so the sign swings in afresh in each room */}
        {plaque ?? (
          <span className="hud-sign" key={roomKey}>
            <span className="hud-plaque" data-testid="room-plaque">{room}</span>
          </span>
        )}
      </div>
      <div className="hud-right" ref={wrapRef}>
        <span className="hud-lanyard" aria-hidden="true" />
        <button
          ref={triggerRef}
          type="button"
          className="hud-chip"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? MENU_ID : undefined}
          aria-label={`${name}, menu`}
          data-testid="player-chip"
          // A reading room can draw before the session opens; the card stays
          // blank until it knows whose it is, rather than flashing "Counsel".
          style={status === 'loading' ? { visibility: 'hidden' } : undefined}
          onClick={() => { SFX.click(); setOpen((v) => !v); }}
          onKeyDown={onTriggerKey}
        >
          <span className="hud-card-stripe" aria-hidden="true" />
          <span className="hud-card-text">
            <span className="hud-chip-name">{name}</span>
            <span className="hud-chip-xp" title="Credit">{formatCredit(account?.xp)}<span className="hud-chip-unit"> credit</span></span>
          </span>
          <Icon name="menu" className="hud-card-menu" />
        </button>
        {open && (
          <div id={MENU_ID} role="menu" aria-label="Destinations" className="hud-menu" data-testid="hud-menu" onKeyDown={onMenuKey}>
            <span className="hud-menu-head">
              <span aria-hidden="true">Directory</span>
              <button
                type="button"
                className="hud-menu-close"
                aria-label="Close the directory"
                data-testid="hud-menu-close"
                onClick={() => { SFX.click(); close(); triggerRef.current?.focus(); }}
              >
                <Icon name="close" />
              </button>
            </span>
            {[...NAV_LINKS, ...MORE_LINKS].map((l) => {
              const k = n; n += 1;
              return (
                <GuardedLink
                  key={l.to}
                  ref={(el) => reg(el, k)}
                  to={l.to}
                  guardLabel={l.label}
                  role="menuitem"
                  tabIndex={-1}
                  className="hud-menu-item ui"
                  aria-current={location.pathname === l.to ? 'page' : undefined}
                  onClick={() => { SFX.click(); close(); }}
                >
                  {l.label}
                </GuardedLink>
              );
            })}
            <hr className="rule-px" />
            <button
              ref={(el) => reg(el, n)}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="hud-menu-item ui"
              aria-label={theme === 'light' ? 'Switch to dark' : 'Switch to light'}
              data-testid="header-theme-toggle"
              onClick={() => { SFX.click(); toggleTheme(); }}
            >
              <Icon name={theme === 'light' ? 'moon' : 'sun'} /> {theme === 'light' ? 'Night' : 'Day'}
            </button>
            <button
              ref={(el) => reg(el, n + 1)}
              type="button"
              role="menuitemcheckbox"
              aria-checked={prefs.sfx}
              tabIndex={-1}
              className="hud-menu-item ui"
              data-testid="hud-sound-toggle"
              onClick={() => { setSfxEnabled(!prefs.sfx); SFX.click(); }}
            >
              <Icon name={prefs.sfx ? 'sound' : 'mute'} /> Sound {prefs.sfx ? 'on' : 'off'}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
